import graphql from "babel-plugin-relay/macro";
import { DatePicker } from "components/shared/datePicker/DatePicker";
import { UpsertAnimalInput } from "domain/animal/upsertAnimal";
import { Rabies_account$key } from "generatedQueries/Rabies_account.graphql";
import { Rabies_configuration$key } from "generatedQueries/Rabies_configuration.graphql";
import { useEffect, useMemo, VFC } from "react";
import { FormState, useFormContext, useWatch } from "react-hook-form";
import { IntlShape, useIntl } from "react-intl";
import { useFragment } from "react-relay";
import { FormLabel } from "shared/components/formInputs/FormLabel";
import { Message } from "shared/components/Message";
import { FormSelect } from "shared/components/newFormInputs/FormSelect";
import { HiddenField } from "shared/components/newFormInputs/HiddenField";
import { S3Upload, UploadStatus } from "shared/components/newFormInputs/S3Upload";
import { ISelectOption } from "shared/components/ReactSelect";
import { datePlus, getValidatedDateOrNull } from "shared/dateFunctions";
import { mapMessages } from "shared/mapMessages";
import { combine, customValidator, isGreaterThan, required, messages as validationMessages } from "shared/validations/validations";

const messages = mapMessages("components.animal.Rabies", {
    immunity12MonthLabel: "1 Year",
    immunity36MonthLabel: "3 Years",
    immunityPeriodLabel: "Rabies vaccination effective for",
    rabiesExpiredValidationMessage: "Rabies vaccination is expired. Please either correct the vaccination date/effective period, or have your pet vaccinated before proceeding.",
    sectionHeading: "Proof of Rabies",
    uploadLabel: "Please upload proof of rabies vaccination",
    vaccinationDateLabel: "Rabies vaccination date",
});

interface IGraphProps {
    configurationKey: Rabies_configuration$key | null;
    accountKey: Rabies_account$key;
}

export const Rabies: VFC<IGraphProps> = ({
    configurationKey,
    accountKey,
}) => {
    const configuration = useFragment(configurationFragment, configurationKey);
    const account = useFragment(accountFragment, accountKey);
    const intl = useIntl();

    const { control, register, trigger, setValue } = useFormContext<UpsertAnimalInput>();

    const dateAdministered = useWatch({ control, name: "rabiesVaccinationDateAdministered" });

    useEffect(() => {
        if (dateAdministered) {
            trigger("rabiesVaccinationImmunityPeriodMonths");
        }
    }, [dateAdministered, trigger]);

    const rabiesImmunisationPeriods = useMemo<ISelectOption[]>(() => {
        return [
            {
                label: intl.formatMessage(messages.immunity12MonthLabel),
                value: 12,
            },
            {
                label: intl.formatMessage(messages.immunity36MonthLabel),
                value: 36,
            },
        ];
    }, [intl]);

    const allowedFileExtensions = useMemo(() => {
        return configuration?.proofOfRabiesAllowedFileExtensions?.map(e => e) ?? [];
    }, [configuration]);

    return (
        <section aria-labelledby="rabies-heading">
            <h3 id="rabies-heading" className="h5">
                <Message message={messages.sectionHeading} />
            </h3>

            <FormLabel
                message={messages.uploadLabel}
                required={true}
                for="rabiesVaccinationFileObjectKey"
            >
                <S3Upload
                    allowedFileExtensions={allowedFileExtensions}
                    name="rabiesVaccinationFileObjectKey"
                    rules={customValidator<UpsertAnimalInput, "rabiesVaccinationFileObjectKey">({ required: rabiesFileUploadValidator(intl) })}
                    onChange={onRabiesUploadChange}
                    getInitialStatus={getInitialUploadStatus} />
                <HiddenField
                    id="rabiesVaccinationStorageId"
                    name="rabiesVaccinationStorageId"
                    data-testid="rabiesVaccinationStorageId"
                    register={register} />
            </FormLabel>

            <FormLabel
                message={messages.vaccinationDateLabel}
                required={true}
                for="rabiesVaccinationDateAdministered"
            >
                <DatePicker<UpsertAnimalInput, "rabiesVaccinationDateAdministered">
                    id="rabiesVaccinationDateAdministered"
                    data-cy="rabiesVaccinationDateAdministered"
                    name="rabiesVaccinationDateAdministered"
                    control={control}
                    rules={required<UpsertAnimalInput, "rabiesVaccinationDateAdministered">()}
                    aria-required={true}
                />
            </FormLabel>

            <FormLabel
                message={messages.immunityPeriodLabel}
                required={true}
                for="rabiesVaccinationImmunityPeriodMonths"
            >
                <FormSelect<UpsertAnimalInput, "rabiesVaccinationImmunityPeriodMonths">
                    control={control}
                    className="rabies-immunity-period"
                    name="rabiesVaccinationImmunityPeriodMonths"
                    options={rabiesImmunisationPeriods}
                    isSearchable={false}
                    rules={combine<UpsertAnimalInput, "rabiesVaccinationImmunityPeriodMonths">(
                        required(),
                        isGreaterThan(intl.formatMessage, 0),
                        customValidator({ isExpired: rabiesExpiryDateValidator(intl) })
                    )}
                    aria-required={true}
                />
            </FormLabel>
        </section>
    );

    function onRabiesUploadChange() {
        setValue("rabiesVaccinationStorageId", null);
    }
};

function rabiesExpiryDateValidator(intl: IntlShape) {
    return (rabiesVaccinationImmunityPeriodMonths: number | undefined, formValues: UpsertAnimalInput) => {
        const dateString = formValues.rabiesVaccinationDateAdministered;
        const dateAdministered = getValidatedDateOrNull(dateString ?? "");

        if (!dateAdministered) {
            return undefined;
        }

        return isRabiesExpired(dateAdministered, rabiesVaccinationImmunityPeriodMonths ?? 12)
            ? intl.formatMessage(messages.rabiesExpiredValidationMessage)
            : undefined;
    };
}

function isRabiesExpired(dateAdministered: Date, immunityPeriod: number) {
    return datePlus(dateAdministered, immunityPeriod, "months") < new Date();
}

function getInitialUploadStatus(formState: FormState<UpsertAnimalInput>): UploadStatus | null {
    if (formState.defaultValues?.rabiesVaccinationStorageId || formState.defaultValues?.rabiesVaccinationFileObjectKey) {
        return UploadStatus.Existing;
    }

    return null;
}

function rabiesFileUploadValidator(intl: IntlShape) {
    return (fileObjectKey: UpsertAnimalInput["rabiesVaccinationFileObjectKey"], formValues: UpsertAnimalInput) => {
        if (fileObjectKey || formValues.rabiesVaccinationStorageId) {
            return undefined;
        }

        return intl.formatMessage(validationMessages.required);
    };
}

const configurationFragment = graphql`
    fragment Rabies_configuration on Configuration {
        proofOfRabiesAllowedFileExtensions
    }`;

const accountFragment = graphql`
    fragment Rabies_account on Account {
        id
    }`;
